import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  TextField,
  Checkbox,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  Alert
} from '@mui/material';
import { DeleteOutline as DeleteIcon, Add as AddIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { db } from '../config/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { debounce } from 'lodash';
import { useTeam } from '../contexts/TeamContext';
import MeetingsList from '../components/meetings/MeetingList';
import { EditorState, convertToRaw, convertFromRaw, RichUtils } from 'draft-js';
import ActionItems from '../components/meetings/ActionItems';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import CheckboxList from '../components/meetings/CheckboxList';
import { Editor } from 'react-draft-wysiwyg';
import AISuggestions from '../components/meetings/AISuggestions';
import emptyStateImage from '../assets/EmptyMeeting.png';


export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(() => {
    const savedMeeting = localStorage.getItem('selectedMeeting');
    return savedMeeting ? JSON.parse(savedMeeting) : null;
  });
  const [editableTitle, setEditableTitle] = useState('');
  const { user: currentUser } = useAuth();
  const { teamMembers, selectedMember } = useTeam();
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [actionItems, setActionItems] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  console.log('Meetings component render:', {
    hasMeetings: meetings.length > 0,
    hasSelectedMeeting: !!selectedMeeting,
    selectedMemberEmail: selectedMember?.email
  });

  useEffect(() => {
    console.log('Meetings component mounted with:', {
      currentUser,
      selectedMember,
      meetings
    });

    return () => {
      console.log('Meetings component unmounted');
    };
  }, []);

  useEffect(() => {
    if (selectedMeeting) {
      localStorage.setItem('selectedMeeting', JSON.stringify(selectedMeeting));
    } else {
      localStorage.removeItem('selectedMeeting');
    }
  }, [selectedMeeting]);

  // Safe date formatting function
  const formatDate = (date) => {
    try {
      // Handle Firestore Timestamp
      if (date && typeof date === 'object' && date.toDate) {
        date = date.toDate();
      }

      // Check if date is valid
      if (!(date instanceof Date) || isNaN(date)) {
        console.log('Invalid date:', date);
        return 'Invalid Date';
      }

      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }).replace(/(\d+)/, (match) => {
        const day = parseInt(match);
        if (isNaN(day)) return match;
        const suffix = ['th', 'st', 'nd', 'rd'][(day % 10 > 3 ? 0 : day % 10)];
        return day + suffix;
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  // Fetch meetings when selected member changes
  useEffect(() => {
    const fetchMeetings = async () => {
      if (!currentUser?.uid || !selectedMember?.id) {
        setMeetings([]);
        setSelectedMeeting(null);
        return;
      }

      try {
        const q = query(
          collection(db, 'meetings'),
          where('managerId', '==', currentUser.uid),
          where('participantId', '==', doc(db, 'teams', selectedMember.id)),
          orderBy('date', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const meetingsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || new Date()
        }));

        setMeetings(meetingsData);

        // Handle meeting selection
        const savedMeeting = localStorage.getItem('selectedMeeting');
        if (savedMeeting) {
          const parsedMeeting = JSON.parse(savedMeeting);
          const existingMeeting = meetingsData.find(m => m.id === parsedMeeting.id);
          if (existingMeeting) {
            setSelectedMeeting(existingMeeting);
            setEditableTitle(existingMeeting.displayDate || '');
            setEditorState(
              existingMeeting.notes
                ? EditorState.createWithContent(convertFromRaw(existingMeeting.notes))
                : EditorState.createEmpty()
            );
            return;
          }
        }

        // If no saved meeting or saved meeting not found, select the first meeting
        if (meetingsData.length > 0) {
          const firstMeeting = meetingsData[0];
          setSelectedMeeting(firstMeeting);
          setEditableTitle(firstMeeting.displayDate || '');
          setEditorState(
            firstMeeting.notes
              ? EditorState.createWithContent(convertFromRaw(firstMeeting.notes))
              : EditorState.createEmpty()
          );
          localStorage.setItem('selectedMeeting', JSON.stringify(firstMeeting));
        } else {
          setSelectedMeeting(null);
          setEditableTitle('');
          setEditorState(EditorState.createEmpty());
          localStorage.removeItem('selectedMeeting');
        }
      } catch (error) {
        console.error('Error fetching meetings:', error);
      }
    };

    fetchMeetings();
  }, [currentUser?.uid, selectedMember?.id]);

  // Persist selected meeting
  useEffect(() => {
    if (selectedMeeting) {
      localStorage.setItem('selectedMeeting', JSON.stringify(selectedMeeting));
    } else {
      localStorage.removeItem('selectedMeeting');
    }
  }, [selectedMeeting]);

  // Handle adding new meeting
  const handleAddMeeting = async () => {
    try {
      const newMeeting = {
        managerId: currentUser.uid,
        participantId: doc(db, 'teams', selectedMember.id),
        date: serverTimestamp(),
        title: '',
        notes: null,
        talkingPoints: [{ text: '', checked: false }],
        blockers: [{ text: '', checked: false }],
        actionItems: []
      };

      const docRef = await addDoc(collection(db, 'meetings'), newMeeting);
      const newMeetingWithId = {
        ...newMeeting,
        id: docRef.id,
        date: new Date(),
        displayDate: formatDate(new Date())
      };

      setMeetings(prev => [newMeetingWithId, ...prev]);
      setSelectedMeeting(newMeetingWithId);
      setEditableTitle(newMeetingWithId.displayDate || '');
      setEditorState(EditorState.createEmpty());
    } catch (error) {
      console.error('Error adding new meeting:', error);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMeeting) return;

    try {
      await deleteDoc(doc(db, 'meetings', selectedMeeting.id));
      setMeetings(meetings.filter(m => m.id !== selectedMeeting.id));
      setSelectedMeeting(meetings[1] || null);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting meeting:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };



  // Update the debounced update function
  const debouncedUpdate = useRef(
    debounce(async (id, newTitle) => {
      try {
        const meetingRef = doc(db, 'meetings', id);
        await updateDoc(meetingRef, {
          displayDate: newTitle
        });

        // Update local state while preserving the meetings array
        setMeetings(prevMeetings =>
          prevMeetings.map(meeting =>
            meeting.id === id
              ? { ...meeting, displayDate: newTitle }
              : meeting
          )
        );

        // Update selected meeting
        setSelectedMeeting(prev => ({
          ...prev,
          displayDate: newTitle
        }));
      } catch (error) {
        console.error('Error updating meeting title:', error);
      }
    }, 1000)
  ).current;

  // Update editable title when selected meeting changes
  useEffect(() => {
    if (selectedMeeting) {
      setEditableTitle(selectedMeeting.displayDate);
    }
  }, [selectedMeeting]);

  // Handle title change
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setEditableTitle(newTitle);

    // Update the title immediately in the UI
    setSelectedMeeting(prev => ({
      ...prev,
      displayDate: newTitle
    }));

    // Update meetings list immediately
    setMeetings(prevMeetings =>
      prevMeetings.map(meeting =>
        meeting.id === selectedMeeting.id
          ? { ...meeting, displayDate: newTitle }
          : meeting
      )
    );

    // Debounced update to Firestore
    if (selectedMeeting) {
      debouncedUpdate(selectedMeeting.id, newTitle);
    }
  };

  const handleAddAISuggestions = () => {
    // Implement AI suggestions addition logic here
  };

  const handleContentUpdate = (field, value) => {
    // Implement content update logic here
  };

  const handleEditorChange = (newEditorState) => {
    console.log('Editor State Change:', {
      currentContent: convertToRaw(newEditorState.getCurrentContent()),
      selection: newEditorState.getSelection().toJS(),
      hasText: !newEditorState.getCurrentContent().getPlainText().trim().length === 0
    });
    
    setEditorState(newEditorState);
    
    if (selectedMeeting) {
      const updatedMeeting = {
        ...selectedMeeting,
        notes: convertToRaw(newEditorState.getCurrentContent())
      };
      setSelectedMeeting(updatedMeeting);
      updateMeeting(updatedMeeting);
    }
  };

  // Add a keyBinding handler to debug keyboard shortcuts
  const handleKeyCommand = (command, editorState) => {
    console.log('Key Command:', command);
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleEditorChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  // Update the editor initialization effect
  useEffect(() => {
    if (selectedMeeting) {
      try {
        const notes = selectedMeeting.notes;
        if (notes && notes.blocks) {
          // Ensure the notes structure is valid
          const validNotes = {
            blocks: notes.blocks.map(block => ({
              ...block,
              key: block.key || Math.random().toString(36),
              text: block.text || '',
              type: block.type || 'unstyled',
              depth: Number.isInteger(block.depth) ? block.depth : 0,
              inlineStyleRanges: Array.isArray(block.inlineStyleRanges) ? block.inlineStyleRanges : [],
              entityRanges: Array.isArray(block.entityRanges) ? block.entityRanges : [],
              data: {
                textAlign: block.data?.textAlign || 'left'
              }
            })),
            entityMap: notes.entityMap || {}
          };

          const contentState = convertFromRaw(validNotes);
          setEditorState(EditorState.createWithContent(contentState));
        } else {
          setEditorState(EditorState.createEmpty());
        }
      } catch (error) {
        console.error('Error initializing editor:', error);
        setEditorState(EditorState.createEmpty());
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }
  }, [selectedMeeting?.id]);

  const handleActionItemsChange = async (newItems) => {
    if (!selectedMeeting) return;

    try {
      // Update Firestore first
      const meetingRef = doc(db, 'meetings', selectedMeeting.id);
      await updateDoc(meetingRef, {
        actionItems: newItems.map(item => ({
          ...item,
          deadline: item.deadline instanceof Date ? Timestamp.fromDate(item.deadline) : item.deadline
        }))
      });

      // Update local states after successful Firestore update
      const updatedMeeting = {
        ...selectedMeeting,
        actionItems: newItems
      };
      
      // Update selected meeting
      setSelectedMeeting(updatedMeeting);
      
      // Update meetings list
      setMeetings(prevMeetings =>
        prevMeetings.map(meeting =>
          meeting.id === selectedMeeting.id ? updatedMeeting : meeting
        )
      );

      console.log('Action items updated successfully:', {
        meetingId: selectedMeeting.id,
        newItems,
        updatedMeeting
      });
    } catch (error) {
      console.error('Error updating action items:', error);
    }
  };

  const handleTalkingPointsChange = (newItems) => {
    if (!selectedMeeting) return;

    const updatedMeeting = {
      ...selectedMeeting,
      talkingPoints: newItems
    };

    setSelectedMeeting(updatedMeeting);
    updateMeeting(updatedMeeting);
  };

  const handleBlockersChange = (newItems) => {
    if (!selectedMeeting) return;

    const updatedMeeting = {
      ...selectedMeeting,
      blockers: newItems
    };

    setSelectedMeeting(updatedMeeting);
    updateMeeting(updatedMeeting);
  };

  const updateMeeting = async (updatedMeeting) => {
    if (!updatedMeeting) return;

    try {
      const meetingRef = doc(db, 'meetings', updatedMeeting.id);
      await updateDoc(meetingRef, {
        talkingPoints: updatedMeeting.talkingPoints,
        blockers: updatedMeeting.blockers,
        // Add any other fields that might need updating
        displayDate: updatedMeeting.displayDate,
        notes: updatedMeeting.notes
      });

      // Update local state
      setMeetings(prevMeetings =>
        prevMeetings.map(meeting =>
          meeting.id === updatedMeeting.id ? updatedMeeting : meeting
        )
      );
    } catch (error) {
      console.error('Error updating meeting:', error);
    }
  };

  const handleAISuggestions = (selectedSuggestions) => {
    if (!selectedMeeting) return;
    
    // Check if talking points is empty or only contains one empty item
    const isEmptyList = selectedMeeting.talkingPoints.length === 0 || 
      (selectedMeeting.talkingPoints.length === 1 && !selectedMeeting.talkingPoints[0].text);

    const newTalkingPoints = isEmptyList
      ? selectedSuggestions.map(text => ({ text, checked: false }))
      : [...selectedMeeting.talkingPoints, ...selectedSuggestions.map(text => ({ text, checked: false }))];

    handleTalkingPointsChange(newTalkingPoints);
  };

  const handleParticipantChange = (teamMember) => {
    if (!selectedMeeting) return;

    const updatedMeeting = {
      ...selectedMeeting,
      participantId: teamMember ? teamMember.ref : null, // Store the document reference instead of email
      participantName: teamMember ? teamMember.name : '',
      participantRole: teamMember ? teamMember.role : ''
    };

    setSelectedMeeting(updatedMeeting);
    updateMeeting(updatedMeeting);
  };

  console.log('Meetings component rendering with:', {
    currentUser: currentUser?.uid,
    selectedMember: selectedMember?.email,
    meetingsCount: meetings.length
  });

  // Check if there are any team members AND if a team member is selected
  const hasTeamMembers = teamMembers && teamMembers.length > 0;
  const canAddMeeting = hasTeamMembers && selectedMember;

  return (
    <Box sx={{ display: 'flex', height: '100%', px:2 }}>
      <Box sx={{ width: 300, pt: 0, pr: 2, pb: 2, pl: 2, py:3 }}>
        <MeetingsList
          meetings={meetings}
          selectedMeeting={selectedMeeting}
          onMeetingSelect={setSelectedMeeting}
          onAddMeeting={handleAddMeeting}
          disableAdd={!canAddMeeting}
        />
      </Box>

      <Box sx={{ flex: 1, pt: 5, pr: 3, pb: 3, pl: 3 }}>
        {selectedMeeting ? (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <TextField
                value={editableTitle}
                onChange={handleTitleChange}
                variant="standard"
                fullWidth
                sx={{
                  "& .MuiInput-underline:before": {
                    borderBottom: "none", // No underline when not focused
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottom: "none", // No underline on hover
                  },
                  "& .MuiInput-underline:after": {
                    borderBottom: "1px solid black", // Underline when focused
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "24px", // Set your font size here
                  },
                }}
              />
              <Tooltip
                title="Delete this meeting"
                placement="top"
                TransitionProps={{ style: { transitionDuration: '0.15s' } }}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -10], // [x-offset, y-offset] in pixels
                        },
                      },
                    ],
                  },
                }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#242424',
                    }
                  }
                }}
              >
                <IconButton onClick={handleDeleteClick} color="error">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 0, fontWeight: 600 }}>Talking Points</Typography>
                <Button
                  variant="text"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setAiDialogOpen(true)}
                  sx={{
                    ml: 4,
                    backgroundColor: 'grey.100',
                    color: 'text.primary',
                    textTransform: 'none',
                    padding: '6px 16px',
                    border: '1px solid transparent',
                    '&:hover': {
                      ml: 4,
                      backgroundColor: 'common.white',
                      border: '1px solid',
                      borderColor: 'primary.main',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.2rem',
                    }
                  }}
                >
                  Generate with AI
                </Button>
              </Box>
              <CheckboxList
                items={selectedMeeting.talkingPoints}
                onItemsChange={handleTalkingPointsChange}
                placeholder="Add a talking point"
              />
              <AISuggestions
                open={aiDialogOpen}
                onClose={() => setAiDialogOpen(false)}
                onSelect={handleAISuggestions}
                selectedMember={selectedMember}
                previousPoints={selectedMeeting?.talkingPoints.map(p => p.text) || []}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Blockers</Typography>
              <CheckboxList
                items={selectedMeeting.blockers}
                onItemsChange={handleBlockersChange}
                placeholder="Add a blocker"
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Notes</Typography>
              <Paper sx={{ 
                p: 2, 
                minHeight: 200,
                '& .rdw-link-modal': {
                  position: 'absolute',
                  backgroundColor: 'white',
                  border: '1px solid #F1F1F1',
                  boxShadow: '3px 3px 5px #BFBDBD',
                  zIndex: 2000,
                  padding: '10px',
                  minWidth: '300px',
                  minHeight: '100px'
                }
              }}>
                <Editor
                  editorState={editorState}
                  onEditorStateChange={handleEditorChange}
                  handleKeyCommand={handleKeyCommand}
                  toolbar={{
                    options: ['inline', 'list', 'textAlign', 'link', 'colorPicker'],
                    inline: {
                      inDropdown: false,
                      options: ['bold', 'italic', 'underline'],
                      bold: { title: 'Bold' },
                      italic: { title: 'Italic' },
                      underline: { title: 'Underline' },
                    },
                    list: {
                      inDropdown: false,
                      options: ['unordered', 'ordered'],
                      unordered: { title: 'Bullet List' },
                      ordered: { title: 'Numbered List' }
                    },
                    textAlign: {
                      inDropdown: false,
                      options: ['left', 'center', 'right'],
                    },
                    link: {
                      inDropdown: false,
                      showOpenOptionOnHover: true,
                      defaultTargetOption: '_blank',
                      options: ['link', 'unlink'],
                      link: { icon: undefined },
                      unlink: { icon: undefined }
                    },
                    colorPicker: {
                      
                      className: undefined,
                      component: undefined,
                      popupClassName: undefined,
                      colors: ['rgb(97,189,109)', 'rgb(26,188,156)', 'rgb(84,172,210)', 'rgb(44,130,201)',
                        'rgb(147,101,184)', 'rgb(71,85,119)', 'rgb(204,204,204)', 'rgb(65,168,95)', 'rgb(0,168,133)',
                        'rgb(61,142,185)', 'rgb(41,105,176)', 'rgb(85,57,130)', 'rgb(40,50,78)', 'rgb(0,0,0)',
                        'rgb(247,218,100)', 'rgb(251,160,38)', 'rgb(235,107,86)', 'rgb(226,80,65)', 'rgb(163,143,132)',
                        'rgb(239,239,239)', 'rgb(255,255,255)', 'rgb(250,197,28)', 'rgb(243,121,52)', 'rgb(209,72,65)',
                        'rgb(184,49,47)', 'rgb(124,112,107)', 'rgb(209,213,216)'],
                    },
                  }}
                  toolbarClassName="rdw-toolbar"
                  wrapperClassName="rdw-wrapper"
                  editorClassName="rdw-editor"
                  onBlur={() => {
                    console.log('Editor lost focus');
                  }}
                  onFocus={() => {
                    console.log('Editor gained focus');
                  }}
                  onToolbarClick={(toolType) => {
                    console.log('Toolbar click:', toolType);
                  }}
                />
              </Paper>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Action Items</Typography>
              <ActionItems
                items={selectedMeeting.actionItems || []}
                onItemsChange={handleActionItemsChange}
                currentUser={currentUser}
                selectedMember={selectedMember}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: 2
          }}>
            <Box sx={{ mb: 2, alignItems: 'center' }}>
             
              <img src={emptyStateImage} alt="Empty state" />
               <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center' }}>
              No meetings yet
               </Typography>
            
              
            </Box>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Meeting</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this meeting? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}